// Reload orchestration for deploy safety (D-05, D-07, D-08, D-21).
//
// Nothing here ever calls a save. The reload path only waits for saves the user
// already made (in-flight pending saves and debounced autosaves, which it
// flushes), stashes registered edits, and reloads.

import * as Sentry from '@sentry/react';
import { getSnapshot } from '../hooks/usePendingSaves';
import { flushAllAutosaves, hasPendingAutosaves, hasFailedAutosaves } from '../hooks/useAutosave';
import { stashAll } from './unsavedWork';
import { CLIENT_BUILD } from './appCallable';
import { noteAutoReload } from './versionWatch';

export const SAVE_WAIT_CAP_MS = 30000;

const crumb = (message, level = 'info', data) => {
    try {
        if (typeof Sentry.addBreadcrumb === 'function') {
            Sentry.addBreadcrumb({ category: 'deploy-safety', level, message, data });
        }
    } catch {
        // telemetry must never block a reload
    }
};

const defaultStorage = () => (typeof window !== 'undefined' ? window.sessionStorage : undefined);

export function hasBlockingSaves() {
    for (const entry of getSnapshot().values()) {
        if (entry && entry.status === 'pending') return true;
    }
    return hasPendingAutosaves();
}

// Unsaved work the reload cannot carry: a deal save that failed and waits for
// Reopen (its payload lives only in memory), or an autosave whose last attempt
// failed with no retry left. The overlay names it in its note (WR-04).
export function hasUnkeptWork() {
    for (const entry of getSnapshot().values()) {
        if (entry && entry.kind === 'primary-write' && entry.status === 'failed') return true;
    }
    return hasFailedAutosaves();
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export async function waitForSavesToSettle({ timeoutMs = SAVE_WAIT_CAP_MS, pollMs = 100 } = {}) {
    let timedOut = false;
    let capTimer;
    const cap = new Promise((resolve) => {
        capTimer = setTimeout(() => { timedOut = true; resolve(); }, timeoutMs);
    });
    const settle = (async () => {
        await flushAllAutosaves();
        while (!timedOut && hasBlockingSaves()) {
            await sleep(pollMs);
        }
    })();
    await Promise.race([settle, cap]);
    clearTimeout(capTimer);
    if (timedOut && hasBlockingSaves()) return { timedOut: true };
    return { timedOut: false };
}

export function stashForReload({ now = Date.now(), storage = defaultStorage() } = {}) {
    let result;
    try {
        result = stashAll({ now, build: CLIENT_BUILD, storage });
    } catch (err) {
        result = { ok: false, count: 0, error: err?.message || String(err) };
    }
    if (!result.ok) crumb('stash failed', 'warning', { error: result.error });
    return result;
}

export function reloadForDeploy({
    reason,
    automatic,
    reload = () => window.location.reload(),
    storage = defaultStorage(),
    now = Date.now(),
} = {}) {
    if (automatic && storage) noteAutoReload(storage, now);
    crumb(`reload (${reason})`, 'info', { reason, automatic: Boolean(automatic) });
    reload();
}
